import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import DownloadIcon from "@mui/icons-material/Download";
import html2canvas from "html2canvas";
import { ContextSession } from "../../services/ContextSession";
import { obtenerDashboardEnrolamiento } from "./enrolamientoApi";
import { formatIsoDateToEs, safeText } from "./reporteriaUtils";

const CHART_COLORS = [
  "#19498c",
  "#2f7ed8",
  "#0c9a8e",
  "#ad8401",
  "#6c5ce7",
  "#ef4444",
  "#10b981",
  "#f59e0b",
  "#6366f1",
  "#ec4899",
];
const DEPARTAMENTO_COLORS = [
  "#2563eb",
  "#14b8a6",
  "#f97316",
  "#e11d48",
  "#7c3aed",
  "#0ea5e9",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#84cc16",
];
const GENERO_COLORS = {
  FEMENINO: "#ec4899",
  MASCULINO: "#2563eb",
  "NO ESPECIFICADO": "#94a3b8",
};
const OFICINAS_ACTUALES_MAP = {
  1: "TEGUCIGALPA",
  2: "SAN PEDRO SULA",
  3: "LA CEIBA",
  4: "CHOLUTECA",
  5: "SANTA ROSA DE COPAN",
  6: "SANTA BARBARA",
  7: "JUTICALPA",
};
const OFICINAS_HISTORICAS_MAP = {
  1: "TEGUCIGALPA",
  2: "SAN PEDRO SULA",
  5: "LA CEIBA",
  8: "SANTA ROSA",
  9: "CHOLUTECA",
  10: "JUTICALPA",
  11: "SANTA BARBARA",
};
const mapOrigenLabel = (value) => {
  const origen = String(value || "").trim().toUpperCase();
  if (origen === "ACTUAL") return "ACTUAL";
  if (origen === "HISTORICO") return "HISTORICO";
  return origen || "SIN_DATOS";
};
const resolveOfficeName = (rawName, officeId, officeOrigen) => {
  const nombre = safeText(rawName);
  const origen = mapOrigenLabel(officeOrigen);
  const officeIdInt = Number(officeId) || 0;
  let fallback = "";
  if (origen === "ACTUAL") {
    fallback = OFICINAS_ACTUALES_MAP[officeIdInt] || "";
  } else if (origen === "HISTORICO") {
    fallback = OFICINAS_HISTORICAS_MAP[officeIdInt] || "";
  }
  if (fallback) {
    return fallback;
  }
  return nombre || "SIN OFICINA";
};
const ORIGEN_ORDER = {
  ACTUAL: 0,
  HISTORICO: 1,
  SIN_DATOS: 2,
};
const normalizeOfficeNameForAggregate = (value) => {
  const raw = safeText(value).trim().toUpperCase();
  if (!raw) return "SIN OFICINA";
  const normalized = raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (normalized === "SANTA ROSA" || normalized === "SANTA ROSA DE COPAN") {
    return "SANTA ROSA DE COPAN";
  }
  return normalized;
};
const sortAndFilterOficinas = (items = []) => (
  (items || [])
    .sort((a, b) => {
      const orderDiff = (ORIGEN_ORDER[a.origen] ?? 9) - (ORIGEN_ORDER[b.origen] ?? 9);
      if (orderDiff !== 0) return orderDiff;
      const totalDiff = Number(b.total || 0) - Number(a.total || 0);
      if (totalDiff !== 0) return totalDiff;
      return String(a.nombre || "").localeCompare(String(b.nombre || ""), "es", { sensitivity: "base" });
    })
);

const KPI_CARDS = [
  { key: "personas_registradas_hoy", label: "Personas Registradas Hoy" },
  { key: "personas_actualizadas_hoy", label: "Personas Actualizadas Hoy" },
  { key: "personas_registradas_total", label: "Personas Registradas Totales" },
];
const formatDashboardNumber = (value) => Number(value || 0).toLocaleString("en-US");
const normalizeDepartamentoLabel = (value) => (
  safeText(value)
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
);
const trimCanvasRightWhitespace = (sourceCanvas, padding = 12) => {
  const width = Number(sourceCanvas?.width || 0);
  const height = Number(sourceCanvas?.height || 0);
  if (!width || !height) return sourceCanvas;

  const context = sourceCanvas.getContext("2d", { willReadFrequently: true });
  if (!context) return sourceCanvas;

  const { data } = context.getImageData(0, 0, width, height);
  let lastContentX = width - 1;

  const hasVisibleContentInColumn = (x) => {
    for (let y = 0; y < height; y += 1) {
      const index = ((y * width) + x) * 4;
      const alpha = data[index + 3];
      const red = data[index];
      const green = data[index + 1];
      const blue = data[index + 2];
      const isContentPixel = alpha > 0 && (red < 250 || green < 250 || blue < 250);
      if (isContentPixel) return true;
    }
    return false;
  };

  while (lastContentX > 0 && !hasVisibleContentInColumn(lastContentX)) {
    lastContentX -= 1;
  }

  const targetWidth = Math.min(width, Math.max(lastContentX + 1 + padding, 1));
  if (targetWidth >= width) return sourceCanvas;

  const trimmedCanvas = document.createElement("canvas");
  trimmedCanvas.width = targetWidth;
  trimmedCanvas.height = height;
  const trimmedContext = trimmedCanvas.getContext("2d");
  if (!trimmedContext) return sourceCanvas;
  trimmedContext.drawImage(sourceCanvas, 0, 0, targetWidth, height, 0, 0, targetWidth, height);
  return trimmedCanvas;
};

export default function DashboardEnrolamiento() {
  const sessi = useContext(ContextSession);
  const authToken = sessi?.sessionData?.token || "";
  const dashboardRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [savingImage, setSavingImage] = useState(false);
  const [captureMode, setCaptureMode] = useState(false);
  const [error, setError] = useState("");
  const [captureError, setCaptureError] = useState("");
  const [data, setData] = useState(null);

  const handleConsultar = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await obtenerDashboardEnrolamiento(authToken);
      setData(response || null);
    } catch (err) {
      setError(err?.message || "No se pudo cargar el dashboard.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleConsultar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGuardarDashboard = async () => {
    const dashboardNode = dashboardRef.current;
    if (!dashboardNode) return;

    try {
      setSavingImage(true);
      setCaptureMode(true);
      setCaptureError("");

      await new Promise((resolve) => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(resolve);
        });
      });

      const canvas = await html2canvas(dashboardNode, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
        width: dashboardNode.scrollWidth,
        height: dashboardNode.scrollHeight,
        windowWidth: dashboardNode.scrollWidth,
        windowHeight: dashboardNode.scrollHeight,
        scrollX: 0,
        scrollY: -window.scrollY,
      });
      const trimmedCanvas = trimCanvasRightWhitespace(canvas);

      const downloadLink = document.createElement("a");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      downloadLink.href = trimmedCanvas.toDataURL("image/png");
      downloadLink.download = `dashboard-enrolamiento-${timestamp}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (err) {
      setCaptureError(err?.message || "No se pudo guardar la imagen del dashboard.");
    } finally {
      setCaptureMode(false);
      setSavingImage(false);
    }
  };

  const kpis = data?.kpis || {};
  const fechaCorte = safeText(data?.fecha_corte);
  const generoData = useMemo(() => {
    const raw = Array.isArray(data?.graficos?.personas_por_genero)
      ? data.graficos.personas_por_genero
      : [];
    return raw.map((item) => ({
      name: safeText(item?.label) || "No especificado",
      value: Number(item?.total || 0),
    }));
  }, [data]);
  const generoTotal = useMemo(
    () => generoData.reduce((acc, item) => acc + Number(item?.value || 0), 0),
    [generoData],
  );

  const departamentoData = useMemo(() => {
    const raw = Array.isArray(data?.graficos?.personas_por_departamento)
      ? data.graficos.personas_por_departamento
      : [];
    return [...raw]
      .filter((item) => normalizeDepartamentoLabel(item?.label) !== "NO APLICA")
      .map((item) => ({
        departamento: safeText(item?.label) || "SIN DEPARTAMENTO",
        total: Number(item?.total || 0),
      }))
      .sort((a, b) => Number(b.total || 0) - Number(a.total || 0));
  }, [data]);
  const departamentoChartHeight = useMemo(
    () => Math.max((departamentoData.length || 1) * 34, 280),
    [departamentoData],
  );
  const departamentoAxisWidth = useMemo(() => {
    const longestLabelLength = departamentoData.reduce(
      (max, item) => Math.max(max, String(item?.departamento || "").length),
      0,
    );
    return Math.min(Math.max(longestLabelLength * 7, 160), 230);
  }, [departamentoData]);
  const departamentoMaxTotal = useMemo(
    () => departamentoData.reduce((max, item) => Math.max(max, Number(item?.total || 0)), 0),
    [departamentoData],
  );
  const departamentoChartDomainMax = useMemo(() => {
    if (!departamentoMaxTotal) return 100;
    return Math.ceil(departamentoMaxTotal * 1.02);
  }, [departamentoMaxTotal]);
  const renderDepartamentoValueLabel = (props) => {
    const {
      height = 0,
      value = 0,
      width = 0,
      x = 0,
      y = 0,
    } = props || {};
    const label = formatDashboardNumber(value);
    const estimatedTextWidth = label.length * 6.5;
    const canFitInside = width >= estimatedTextWidth + 18;

    return (
      <text
        x={canFitInside ? x + width - 8 : x + width + 6}
        y={y + (height / 2) + 4}
        textAnchor={canFitInside ? "end" : "start"}
        fill={canFitInside ? "#ffffff" : "#1a2f4d"}
        fontSize={11}
        fontWeight={700}
      >
        {label}
      </text>
    );
  };
  const oficinasHoy = useMemo(() => {
    const raw = Array.isArray(data?.oficinas_hoy_detalle) ? data.oficinas_hoy_detalle : [];
    const mapped = raw.map((item) => ({
      key: safeText(item?.oficina_key) || "",
      nombre: resolveOfficeName(item?.oficina_nombre, item?.oficina_id, item?.oficina_origen),
      origen: mapOrigenLabel(safeText(item?.oficina_origen)),
      total: Number(item?.total || 0),
    }));
    return sortAndFilterOficinas(mapped);
  }, [data]);
  const oficinasHoyTotal = useMemo(
    () => oficinasHoy.reduce((acc, item) => acc + Number(item?.total || 0), 0),
    [oficinasHoy],
  );
  const oficinasHistorico = useMemo(() => {
    const raw = Array.isArray(data?.oficinas_historico_detalle)
      ? data.oficinas_historico_detalle
      : [];
    const mapped = raw.map((item) => ({
      key: safeText(item?.oficina_key) || "",
      nombre: resolveOfficeName(item?.oficina_nombre, item?.oficina_id, item?.oficina_origen),
      origen: mapOrigenLabel(safeText(item?.oficina_origen)),
      total: Number(item?.total || 0),
    }));
    const grouped = new Map();
    mapped.forEach((item) => {
      const normalizedName = normalizeOfficeNameForAggregate(item?.nombre);
      if (!grouped.has(normalizedName)) {
        grouped.set(normalizedName, {
          key: `HIST-AGG-${normalizedName}`,
          nombre: normalizedName,
          origen: "",
          total: 0,
        });
      }
      const current = grouped.get(normalizedName);
      current.total += Number(item?.total || 0);
    });

    return Array.from(grouped.values()).sort((a, b) => {
      const totalDiff = Number(b.total || 0) - Number(a.total || 0);
      if (totalDiff !== 0) return totalDiff;
      return String(a.nombre || "").localeCompare(String(b.nombre || ""), "es", { sensitivity: "base" });
    });
  }, [data]);
  const oficinasHistoricoTotal = useMemo(
    () => oficinasHistorico.reduce((acc, item) => acc + Number(item?.total || 0), 0),
    [oficinasHistorico],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Paper ref={dashboardRef} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #dce7f7" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ color: "#19498c", fontWeight: 700 }}>
            Dashboard Enrolamiento
          </Typography>
          {captureMode ? null : (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleGuardarDashboard}
                disabled={loading || savingImage}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                {savingImage ? "Guardando..." : "Guardar Dashboard"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleConsultar}
                disabled={loading || savingImage}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                {loading ? "Actualizando..." : "Actualizar"}
              </Button>
            </Box>
          )}
        </Box>

        <Typography sx={{ color: "#4f5f77", fontSize: 13, mb: 2 }}>
          Fecha de corte: {fechaCorte ? formatIsoDateToEs(fechaCorte) : "N/D"}
        </Typography>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {captureError ? <Alert severity="warning" sx={{ mb: 2 }}>{captureError}</Alert> : null}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {KPI_CARDS.map((card) => (
            <Grid key={card.key} item xs={12} md={4} lg={3}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                <Typography sx={{ color: "#5a6a80", fontSize: 12 }}>
                  {card.label}
                </Typography>
                <Typography sx={{ color: "#1a2f4d", fontWeight: 700, fontSize: 28 }}>
                  {formatDashboardNumber(kpis?.[card.key])}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Typography sx={{ fontWeight: 700, color: "#1a2f4d", mb: 1 }}>
            Oficinas con Registros Hoy
          </Typography>
          <Box>
            {oficinasHoy.length ? (
              oficinasHoy.map((item) => (
                <Box
                  key={item.key || `${item.nombre}-${item.origen}`}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                    py: 0.75,
                    borderBottom: "1px dashed #dbe6f5",
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: "#22334d" }}>
                    {item.nombre}
                    {item.origen && item.origen !== "SIN_DATOS" ? ` (${item.origen})` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#1a2f4d", fontWeight: 700 }}>
                    {formatDashboardNumber(item.total)}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography sx={{ color: "#6b7b91", fontSize: 13 }}>
                Sin registros por oficina para hoy.
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 1,
              pt: 1,
              borderTop: "1px solid #c8d7ee",
            }}
          >
            <Typography sx={{ color: "#1a2f4d", fontWeight: 700 }}>
              Total
            </Typography>
            <Typography sx={{ color: "#1a2f4d", fontWeight: 800 }}>
              {formatDashboardNumber(oficinasHoyTotal)}
            </Typography>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Typography sx={{ fontWeight: 700, color: "#1a2f4d", mb: 1 }}>
            Oficinas con Registros Históricos de Afiliación
          </Typography>
          <Box>
            {oficinasHistorico.length ? (
              oficinasHistorico.map((item) => (
                <Box
                  key={item.key || `${item.nombre}-${item.origen}-hist`}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                    py: 0.75,
                    borderBottom: "1px dashed #dbe6f5",
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: "#22334d" }}>
                    {item.nombre}
                    {item.origen && item.origen !== "SIN_DATOS" ? ` (${item.origen})` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#1a2f4d", fontWeight: 700 }}>
                    {formatDashboardNumber(item.total)}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography sx={{ color: "#6b7b91", fontSize: 13 }}>
                Sin registros históricos por oficina.
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 1,
              pt: 1,
              borderTop: "1px solid #c8d7ee",
            }}
          >
            <Typography sx={{ color: "#1a2f4d", fontWeight: 700 }}>
              Total Histórico
            </Typography>
            <Typography sx={{ color: "#1a2f4d", fontWeight: 800 }}>
              {formatDashboardNumber(oficinasHistoricoTotal)}
            </Typography>
          </Box>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 360 }}>
              <Typography sx={{ fontWeight: 700, color: "#1a2f4d", mb: 1 }}>
                Personas Registradas por Género
              </Typography>
              <Box sx={{ height: "90%", display: "flex", flexDirection: "column" }}>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={generoData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="48%"
                        innerRadius={58}
                        outerRadius={100}
                        paddingAngle={2}
                        labelLine={false}
                        label={false}
                      >
                        {generoData.map((entry, index) => (
                          <Cell
                            key={`${entry.name}-${index}`}
                            fill={GENERO_COLORS[entry.name?.toUpperCase?.()] || CHART_COLORS[index % CHART_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value) => Number(value || 0)} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap", mt: 0.5 }}>
                  {generoData.map((item) => {
                    const total = Number(item?.value || 0);
                    const percent = generoTotal > 0 ? ((total / generoTotal) * 100).toFixed(1) : "0.0";
                    const color = GENERO_COLORS[item.name?.toUpperCase?.()] || "#64748b";
                    return (
                      <Box key={`legend-${item.name}`} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color }} />
                        <Typography sx={{ fontSize: 12, color: "#1f3656", fontWeight: 700 }}>
                          {item.name}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#5b6b82" }}>
                          {total.toLocaleString()} ({percent}%)
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minHeight: 360 }}>
              <Typography sx={{ fontWeight: 700, color: "#1a2f4d", mb: 1 }}>
                Personas por Departamento
              </Typography>
              <Box sx={{ height: departamentoChartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={departamentoData}
                    layout="vertical"
                    margin={{ top: 8, right: 28, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="4 4" stroke="#d4e0f5" />
                    <XAxis
                      type="number"
                      domain={[0, departamentoChartDomainMax]}
                      tick={{ fontSize: 11, fill: "#5b6b82" }}
                      tickFormatter={formatDashboardNumber}
                      axisLine={{ stroke: "#b7c8e5" }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="departamento"
                      width={departamentoAxisWidth}
                      interval={0}
                      tick={{ fontSize: 11, fill: "#2d3d57", fontWeight: 600 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RechartsTooltip
                      formatter={(value) => formatDashboardNumber(value)}
                      cursor={{ fill: "rgba(25, 73, 140, 0.08)" }}
                      contentStyle={{
                        borderRadius: 10,
                        border: "1px solid #cfe0fb",
                        boxShadow: "0 6px 20px rgba(25, 73, 140, 0.12)",
                      }}
                    />
                    <Bar dataKey="total" radius={[0, 8, 8, 0]} barSize={18}>
                      {departamentoData.map((entry, index) => (
                        <Cell
                          key={`dep-bar-${entry.departamento}-${index}`}
                          fill={DEPARTAMENTO_COLORS[index % DEPARTAMENTO_COLORS.length]}
                        />
                      ))}
                      <LabelList
                        dataKey="total"
                        content={renderDepartamentoValueLabel}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
